import { useCallback, useEffect, useRef, useState } from 'react'
import firebase from '../firebase/compat'

// De aantallen en het voorbeeld van de updatemailing, via de callable
// getUpdatesMailingPreview. Geen Firestore-listener: de lijst gaat over álle
// organisaties en álle gebruikers, en die staan achter tenant-regels — de
// beheertenant zou in de browser een lege lijst krijgen, geen fout.
//
// `error` blijft staan als de aanroep faalt. Stil terugvallen op nul ontvangers
// zou op het scherm lezen als "er is niemand om te mailen", en dat is precies
// de verkeerde conclusie vlak voor je op verzenden drukt.
// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
//
// AI-CONTEXT: See docs/features/updates-mailing.md

/**
 * @typedef {object} MailingConcept
 * @property {Object<string, string>} [subjects] - Onderwerp per merk-id
 * @property {string} [subject] - Onderwerp voor alle merken; alleen bij het openen, vóór de merken bekend zijn
 * @property {string} intro - De getypte introtekst
 * @property {string} actionLabel - Wat er op de knop staat
 */

/**
 * @param {MailingConcept} initieel - Waarmee het voorbeeld bij het openen wordt opgehaald
 * @returns {{data: object|null, loading: boolean, error: string|null,
 *   refresh: (concept: MailingConcept) => Promise<void>,
 *   markBrandResolved: (docId: string) => void}}
 */
const useUpdatesMailingPreview = (initieel) => {

    const [data, setData] = useState(/** @type {object | null} */ (null))
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(/** @type {string | null} */ (null))

    // In een ref en niet in de dependency-array: `initieel` is een objectliteral
    // en dus elke render een nieuwe waarde, wat het effect eindeloos zou laten
    // vuren. AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const eerste = useRef(initieel)

    /** @type {React.MutableRefObject<boolean>} */
    const levend = useRef(true)
    useEffect(() => () => { levend.current = false }, [])

    const refresh = useCallback(async (concept) => {
        setLoading(true)
        setError(null)
        try {
            // v2-functie in europe-west1 — region expliciet, anders belt de SDK
            // us-central1 en bestaat de functie daar niet.
            const getUpdatesMailingPreview = firebase.app().functions('europe-west1')
                .httpsCallable('getUpdatesMailingPreview')
            const result = await getUpdatesMailingPreview(concept || {})
            if (!levend.current) return
            setData(result.data || null)
        } catch (err) {
            console.error('useUpdatesMailingPreview error:', err)
            if (!levend.current) return
            // Altijd met een zin eromheen. Onze eigen fouten dragen een
            // bruikbare tekst ("Alleen de Deccos-beheertenant mag..."), maar bij
            // een functie die niet bestaat of niet bereikbaar is geeft Firebase
            // alleen de code terug -- en dan staat er letterlijk 'internal' op
            // het scherm.
            setError(`Kon het voorbeeld niet ophalen: ${err.message || 'onbekende fout'}.`)
        } finally {
            if (levend.current) setLoading(false)
        }
    }, [])

    useEffect(() => { refresh(eerste.current) }, [refresh])

    /**
     * Eén organisatie uit de lijst 'nog geen merk' halen na een geslaagde
     * keuze — bewust NIET het hele voorbeeld opnieuw ophalen: dat wist de
     * introtekst die de gebruiker net getypt heeft nog niet, maar het kost wel
     * een volle scan over alle gebruikers voor elke rij die je invult.
     *
     * @param {string} docId - Document-id van het CompagnyMeta-document
     */
    const markBrandResolved = (docId) => {
        setData((huidig) => {
            if (!huidig) return huidig
            const over = (huidig.unknownBrandTenants || []).filter((t) => t.docId !== docId)
            return { ...huidig, unknownBrandTenants: over }
        })
    }

    return { data, loading, error, refresh, markBrandResolved }
}

export default useUpdatesMailingPreview
